import { Diamond, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { INITIATIVE_STATUSES } from '@complan/contracts';
import type { InitiativeStatus } from '@complan/domain';
import { DiamondAlert } from '@/shared/components/DiamondAlertIcon';
import { STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import type { PlatformLane } from './grid-model';
import {
  STATUS_LETTER_KEYS,
  barTitleColour,
  effectiveBarFill,
  hatchCss,
  renderedBarFill,
  resolveStatusStyle,
} from './lane-layer';

/**
 * The Platform colour the status swatches demonstrate the treatments on.
 *
 * A literal rather than `var(--platform-p1)`, because the swatch now derives its
 * painted fill and its letter ink in JS — Draft lightens the colour and the ink is
 * chosen by contrast against the result — and neither computation can see through a
 * CSS custom property. Mirrors `--platform-p1` in `styles/theme.css`; the swatch is
 * illustrative, so a drift here misrepresents nothing beyond the demo hue, and the
 * legend's contrast test asserts the letter stays legible regardless.
 */
const SWATCH_PLATFORM_COLOUR = '#0f6fbf';

/**
 * The mandatory Calendar_View legend, anchored variant (UI spec §3.9, §4.5, task
 * 22.3).
 *
 * §4.5 makes this legend non-optional: it is what guarantees colour is never the
 * only carrier of meaning (R10.5, R10.6, R18.8). It carries a swatch and name for
 * every primary Platform in the current rendering (not the full master data — the
 * grid model's lanes are exactly the rendered Platforms, so the legend can never
 * name a Platform absent from the view), the status encoding stated *in words*,
 * and a glyph key for the milestone diamond, the elevated-priority alert diamond,
 * the confidential lock and the current-week rule.
 *
 * Two of those glyphs are diamonds, which is intentional rather than an oversight:
 * the elevated-priority marker carries an exclamation the milestone marker does
 * not, and each entry names itself in words right beside the glyph — the legend is
 * the thing that makes that pair decodable.
 *
 * Only the Platform `name` is shown, never its `code` — the lowercase internal
 * identifier master data stores for export round-tripping and uniqueness
 * (`packages/api/migrations/0002_master_data_tables.sql`). R10.5 names the
 * Platform *name* as the legend's text carrier; showing the code as well would
 * surface an internal identifier a user never entered as a display value.
 *
 * ## One entry per status (UI spec §4.5, open decision 1 resolved in spec v1.3)
 *
 * The click dummy's legend said "faded = Planned", and the encoding it described
 * folded Draft, Submitted and Planned onto one visual. Both are gone: every status
 * now names itself on the bar and carries its own hatch density, and this legend
 * enumerates all six, each showing its letter mark and texture in a miniature bar
 * and naming its treatment in words.
 *
 * Because the bar states its status as a word, this legend is no longer the thing
 * that makes status *decodable* — it explains the reinforcing channels (hatch
 * density, opacity, the terminal-state treatments) rather than serving as a
 * codebook for six symbols. It stays mandatory: it is still the carrier for the
 * Platform colours and the marker glyphs.
 *
 * Each swatch is rendered from the same {@link resolveStatusStyle} the bars use,
 * never from hand-copied styles, so the legend cannot describe a treatment the
 * calendar does not paint. Wording drifting from the rendering is precisely the
 * defect the stale "faded = Planned" text was.
 *
 * Anchored styling per §3.9: `--surface-alt` background, a 1px `--line` top
 * border, `9px 12px` padding, 11px `--muted` text, 14px gap, wrapping, with the
 * emphasised term in `--txt`.
 */
export interface CalendarLegendProps {
  /** The Platform lanes of the current rendering; one swatch per lane (R10.5). */
  readonly lanes: readonly PlatformLane[];
  /** Whether the current-week rule is shown (only in the current planning year, R10.12). */
  readonly showsCurrentWeek: boolean;
}

export function CalendarLegend({
  lanes,
  showsCurrentWeek,
}: CalendarLegendProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="border-line flex flex-wrap gap-x-[14px] gap-y-2 border-t px-3 py-[9px] text-[11px] text-muted-foreground"
      style={{ background: 'var(--surface-alt)' }}
      aria-label={t('legend.ariaLabel')}
    >
      {/* Platform swatches (§4.5, R10.5) */}
      {lanes.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <b className="text-txt">{t('legend.platforms')}</b>
          {lanes.map((lane) => (
            <span key={lane.platformId} className="flex items-center gap-[5px]">
              <span
                aria-hidden
                className="inline-block h-[10px] w-[10px] rounded-[2px]"
                style={{ background: lane.colour }}
              />
              <span>
                <b className="text-txt">{lane.name}</b>
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {/* Status encoding in words, one entry per status of R9.1 (§4.5, R10.6). */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <b className="text-txt">{t('legend.status')}</b>
        {INITIATIVE_STATUSES.map((status) => (
          <span key={status} className="flex items-center gap-[5px]">
            <StatusSwatch status={status} />
            <span>
              <b className="text-txt">{t(STATUS_LABEL_KEYS[status])}</b>{' '}
              {t(`legend.statusTreatment.${status}`)}
            </span>
          </span>
        ))}
      </div>

      {/* Glyph key (§4.5): milestone · priority · confidential · current week. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <b className="text-txt">{t('legend.markers')}</b>
        <span className="flex items-center gap-[5px]">
          <Diamond aria-hidden className="size-[11px]" strokeWidth={2} />
          {t('legend.milestone')}
        </span>
        {/* The bar's own marker at the bar's own size and stroke weight — 15px,
            larger than the neighbouring 11px glyphs — because the legend is a glyph
            key: a mismatch here is a mismatch the reader has to decode. */}
        <span className="flex items-center gap-[5px]">
          <DiamondAlert aria-hidden className="size-[15px]" strokeWidth={2.5} />
          {t('legend.elevatedPriority')}
        </span>
        <span className="flex items-center gap-[5px]">
          <Lock aria-hidden className="size-[11px]" strokeWidth={2.5} />
          {t('legend.confidential')}
        </span>
        {showsCurrentWeek ? (
          <span className="flex items-center gap-[5px]">
            <span
              aria-hidden
              className="inline-block h-[12px] w-[2px]"
              style={{ background: 'var(--risk)', opacity: 0.55 }}
            />
            {t('legend.currentPeriod')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A miniature bar showing exactly how a status paints: its letter mark, its fill
 * treatment (pale with a dashed outline / pale / pale with a soft hatch / solid), its
 * opacity and — for Final and Cancelled — the inset border and the grey override.
 *
 * Driven by the same {@link resolveStatusStyle} the bars are, rather than by
 * hand-copied styles, so the legend cannot claim a treatment the calendar does not
 * render. That is precisely the failure the stale "faded = Planned" wording was an
 * instance of.
 *
 * The swatch shows the single-letter mark rather than the full word: at 20px it is
 * the tier a real bar of that width would use, so the swatch stays an honest
 * miniature. The word itself is spelled out immediately beside it in the legend row.
 */
function StatusSwatch({ status }: { readonly status: InitiativeStatus }): React.JSX.Element {
  const { t } = useTranslation();
  const style = resolveStatusStyle(status);
  const base = style.colourOverride ?? SWATCH_PLATFORM_COLOUR;
  // The same derivations the bar runs, from the same functions, so the swatch cannot
  // show a fill, a hatch or an ink the calendar does not paint.
  const paintedFill = effectiveBarFill(base, style.fillLightenAlpha);
  const letterInk =
    style.textColour ?? barTitleColour(renderedBarFill(SWATCH_PLATFORM_COLOUR, style));
  return (
    <span
      aria-hidden
      className="inline-flex h-[14px] w-[20px] items-center justify-center rounded-[7px] text-[8px] font-bold"
      style={{
        backgroundColor: paintedFill,
        backgroundImage:
          style.hatch === null ? 'none' : hatchCss(style.hatch, SWATCH_PLATFORM_COLOUR),
        borderStyle: style.borderStyle,
        borderWidth: `${String(style.borderWidthPx)}px`,
        borderColor: style.borderUsesPlatformColour
          ? SWATCH_PLATFORM_COLOUR
          : 'rgba(0,0,0,0.12)',
        opacity: style.opacity,
        color: letterInk,
        ...(style.insetBorder !== null && { boxShadow: style.insetBorder }),
      }}
    >
      {t(STATUS_LETTER_KEYS[status])}
    </span>
  );
}
